import { PkDivider, PkMatchHeader } from '@uefadigital/panenka-react';
import clsx from 'clsx';
import { memo } from 'react';

import { useWindowSize } from '../hooks/useWindowSize';
import Broadcasters from '../MatchUnit/Broadcasters';
import useMatch from '../MatchUnit/useMatch';
import useMatchBroadcasters from '../MatchUnit/useMatchBroadcasters';
import { useMatchUnitTranslations } from '../MatchUnit/useMatchUnitTranslations';
import type { BroadcastersParams } from '../queries/broadcasters';

import type { Match } from '@shared-types/match.types';
import { MatchPhase, MatchStatus } from '@shared-types/match.types';
import { language } from '@utilities/browser';
import { formatUrl } from '@utilities/fetch';

import * as classes from './styles.module.scss';

export type MatchHeaderProps = {
  match: Match;
  options: {
    broadcasters?: BroadcastersParams;
    disablePlayerLink?: boolean;
    disableTeamsLink?: boolean;
    showSeason?: boolean;
    customTimeZone?: string;
  };
  isCompactMode?: boolean;
  className?: string;
};

const MatchHeaderWrap = ({ match: originalMatch, options, className, isCompactMode }: MatchHeaderProps) => {
  const { disablePlayerLink, disableTeamsLink, showSeason, customTimeZone } = options;
  const { isMobile, isTablet } = useWindowSize();

  const match = useMatch(originalMatch, true);

  const redCards = match.playerEvents?.redCards;
  const scorers = match.playerEvents?.scorers;
  const penaltiesMissed = match.playerEvents?.penaltiesMissed;
  const isLiveMatchPenalties = match.status === MatchStatus.LIVE && match.phase === MatchPhase.PENALTY;

  const noevents = !redCards?.length && !scorers?.length && !penaltiesMissed?.length && !isLiveMatchPenalties;

  const { translations, dateCalendarFormat } = useMatchUnitTranslations(showSeason);

  const teamServiceUrl = formatUrl(null, '/api/v1/linkrules/team/{{teamId}}/', {
    competitionId: match.competition?.id,
    phase: match.round?.phase,
  });
  const playerServiceUrl = formatUrl(null, '/api/v1/linkrules/player/{{playerId}}/', {
    competitionId: match.competition?.id,
    phase: match.round?.phase,
  });

  const broadcasters = useMatchBroadcasters(match, options, !isCompactMode);

  if (!match?.id || !match?.competition?.id) return null;

  return (
    <>
      <PkMatchHeader
        className={clsx(className, classes.header)}
        matchData={match}
        language={language.toUpperCase() as any}
        isCompactMode={isCompactMode}
        dateCalendarFormat={dateCalendarFormat}
        disablePlayerLink={disablePlayerLink}
        disableTeamsLink={disableTeamsLink}
        translations={translations}
        customTimeZone={customTimeZone}
        teamServiceUrl={teamServiceUrl}
        playerServiceUrl={playerServiceUrl}
      />
      {!isCompactMode && (
        <div className={clsx(classes.mhBroadcasters, noevents ? classes.mhNoEvents : 'pk-mt-md--s pk-mt--xs')}>
          {(isMobile || isTablet) && broadcasters?.length > 0 && <PkDivider direction='horizontal' />}
          <Broadcasters
            broadcasters={broadcasters}
            showLabel
            className='pk-justify-content--center pk-mt--xs pk-mt-md--0'
          />
        </div>
      )}
    </>
  );
};

export default memo(MatchHeaderWrap);
